import { useState } from "react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { sendPasswordResetEmail } from "firebase/auth";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notification } from "@/components/admin/notification";
import { AuthVisualPanel } from "./AuthVisualPanel";
import { auth } from "@/config/firebase";
import { logger } from "@/utils/logger";

// glowtest-aligned email validation (authenticate.js validateEmail)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Password reset page (Firebase sendPasswordResetEmail).
 *
 * Always shows the same generic confirmation regardless of whether the email
 * exists (auth/user-not-found is swallowed) to avoid account enumeration.
 * The reset link lands back on /login per the action-handler default.
 */
export const ForgotPasswordPage = () => {
  const translate = useTranslate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = EMAIL_REGEX.test(email.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || loading) return;
    const start = performance.now();
    logger.info("[FORGOT_PW][handleSubmit_010] reset requested");
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email.trim(), {
        url: `${window.location.origin}/login`,
      });
      logger.info("[FORGOT_PW][handleSubmit_100] reset email sent", {
        duration_ms: Math.round(performance.now() - start),
      });
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/user-not-found" || code === "auth/invalid-email") {
        // Generic success — do not reveal whether the account exists.
        logger.info("[FORGOT_PW][handleSubmit_090] reset suppressed (unknown email)");
      } else {
        logger.error("[FORGOT_PW][handleSubmit_095] reset failed", err instanceof Error ? err : undefined);
        setError("Something went wrong. Please try again in a moment.");
        setLoading(false);
        return;
      }
    }
    setSent(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <AuthVisualPanel />
        <div className="flex flex-col justify-center w-full p-4 lg:p-8">
          <div className="w-full space-y-6 lg:mx-auto lg:w-[350px]">
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                {translate("crm.auth.forgot_password.title", {
                  _: "Reset your password",
                })}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {translate("crm.auth.forgot_password.subtitle", {
                  _: "Enter your account email and we'll send you a reset link.",
                })}
              </p>
            </div>

            {sent ? (
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.auth.forgot_password.sent", {
                    _: "If an account exists for this email, you'll receive a password reset link shortly. Check your inbox.",
                  })}
                </p>
                <Link
                  to="/login"
                  className="block text-sm text-center hover:underline"
                >
                  {translate("crm.auth.request_access.back_to_login", {
                    _: "Back to sign in",
                  })}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">
                    {translate("ra.auth.email", { _: "Email" })}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {!isValid && email.trim() ? (
                    <p className="text-sm text-destructive">
                      {translate("crm.auth.forgot_password.invalid_email", {
                        _: "Invalid email format",
                      })}
                    </p>
                  ) : null}
                </div>
                {error ? (
                  <p className="text-sm text-destructive">{error}</p>
                ) : null}
                <Button
                  type="submit"
                  disabled={!isValid || loading}
                  className="w-full cursor-pointer"
                >
                  {loading ? (
                    <Spinner
                      className="text-primary-foreground size-4"
                      data-icon="inline-start"
                    />
                  ) : null}
                  {translate("crm.auth.forgot_password.submit", {
                    _: "Send reset link",
                  })}
                </Button>
                <Link
                  to="/login"
                  className="block text-sm text-center hover:underline"
                >
                  {translate("crm.auth.request_access.back_to_login", {
                    _: "Back to sign in",
                  })}
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};

ForgotPasswordPage.path = "/forgot-password";
